// Astrea Ops Platform - Personnel assignments data-access layer
//
// Phase 3, fifth slice - the first of the nested-area follow-ups the core
// `projects` slice deferred (see go-live-plan.md and src/lib/projects.js's header
// comment). Maps the app's existing `project.personnel[]` shape
// ({id, userId, onsiteStart, onsiteEnd, role, flightIn, flightOut, accommodation,
// notes}) onto the real `assignments` table from
// supabase/migrations/20260915000003_projects_and_lifecycle.sql (person_id,
// onsite_start/end, project_role, flight_in/out, accommodation, notes).
//
// Unlike database/users/unavailability, personnel isn't a flat top-level array -
// it's nested per-project. So this slice works differently: loadAssignments() is
// called once from projects.js's loadProjects() and grouped by project_id, and
// its rows OVERRIDE whatever `personnel` array is sitting in that project's
// app_data bridge column (same "real columns win" rule projects.js already
// applies to the project's own top-level fields). PersonnelPanel's three mutation
// points (add, edit, remove - plus the single-role "replace" case, which is a
// delete-then-create) call the matching function below alongside the existing
// onUpdate(project) call, so the change lands in both the real `assignments` row
// and the project's app_data blob (still written whole on every project update
// until every nested area has its own slice).
//
// project_role uses the same app_role enum/values as everywhere else in the app
// (ROLES in App.jsx) - no translation needed. onsite_start/onsite_end are real
// `date` columns; the app's date-picker fields are always empty or a valid ISO
// date, but a null/invalid value is guarded anyway (same pattern as
// projects.js's event_end_date).

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function safeDate(s) {
  return typeof s === 'string' && ISO_DATE_RE.test(s) ? s : null;
}

// Multiple onsite roles (30 Sep 2026, migration 20260930000002): every role lives in the
// text[] column project_roles; project_role keeps the primary role, limited to values the
// app_role enum accepts (site_visit, management_visit and reviewer are onsite-only tags).
const APP_ROLE_VALUES = new Set(['senior_management', 'pom', 'finance', 'writer', 'team_lead', 'risk_manager', 'medical']);
function entryRoles(entry) {
  const list = Array.isArray(entry.roles) && entry.roles.length ? entry.roles : [entry.role];
  return [...new Set(list.filter(Boolean))];
}
function primaryRole(entry) {
  const roles = entryRoles(entry);
  return roles.find(r => APP_ROLE_VALUES.has(r)) || null;
}
function entryToColumns(entry) {
  return {
    person_id: entry.userId,
    project_role: primaryRole(entry),
    project_roles: entryRoles(entry),
    onsite_start: safeDate(entry.onsiteStart),
    onsite_end: safeDate(entry.onsiteEnd),
    flight_in: entry.flightIn || null,
    flight_out: entry.flightOut || null,
    accommodation: entry.accommodation || null,
    notes: entry.notes || null,
    tentative: !!entry.tbc,
  };
}
// Until a migration is applied its column does not exist; retry without the newer columns
// (project_roles 30 Sep 2026, tentative 2 Oct 2026) so saving personnel never breaks because
// of a pending `supabase db push`.
const OPTIONAL_COLUMNS = ['project_roles', 'tentative'];
async function writeWithFallback(run, cols) {
  let { error } = await run(cols);
  let tries = 0;
  while (error && tries < OPTIONAL_COLUMNS.length) {
    const msg = error.message || '';
    const current = cols;
    const missing = OPTIONAL_COLUMNS.filter(c => c in current && msg.includes(c));
    if (!missing.length) break;
    const next = { ...cols };
    missing.forEach(c => { delete next[c]; });
    cols = next;
    ({ error } = await run(cols));
    tries++;
  }
  if (error) throw error;
}

function rowToEntry(row) {
  const roles = Array.isArray(row.project_roles) && row.project_roles.length
    ? row.project_roles
    : (row.project_role ? [row.project_role] : []);
  return {
    id: row.id,
    userId: row.person_id,
    onsiteStart: row.onsite_start || '',
    onsiteEnd: row.onsite_end || '',
    role: roles[0] || row.project_role || '',
    roles,
    flightIn: row.flight_in || '',
    flightOut: row.flight_out || '',
    accommodation: row.accommodation || '',
    notes: row.notes || '',
    tbc: !!row.tentative,
  };
}

// Loads every assignment row and groups it by project_id, ready for
// projects.js's loadProjects() to overlay onto each project's `personnel` array.
export async function loadAssignmentsByProject() {
  const { data, error } = await selectAllRes(() => supabase.from('assignments').select('*').order('id'));
  if (error) throw error;
  const byProject = new Map();
  (data || []).forEach(row => {
    const list = byProject.get(row.project_id) || [];
    list.push(rowToEntry(row));
    byProject.set(row.project_id, list);
  });
  return byProject;
}

// entry must already carry a real UUID id (see the module note above).
export async function assignmentCreate(entry, projectId) {
  await writeWithFallback(
    cols => supabase.from('assignments').insert({ id: entry.id, project_id: projectId, ...cols }),
    entryToColumns(entry),
  );
}

export async function assignmentUpdate(entry) {
  await writeWithFallback(
    cols => supabase.from('assignments').update(cols).eq('id', entry.id),
    entryToColumns(entry),
  );
}

export async function assignmentDelete(id) {
  const { error } = await supabase.from('assignments').delete().eq('id', id);
  if (error) throw error;
}

// Persists whatever changed between two versions of a project's personnel list - used
// wherever personnel is changed outside the Personnel tab (the Proposal calculator's POM
// and Doc Writer pickers, the Overview RM picker), so those changes reach the
// `assignments` table instead of living only in the app_data blob.
const PERSISTED_FIELDS = ['userId', 'onsiteStart', 'onsiteEnd', 'flightIn', 'flightOut', 'accommodation', 'notes'];
const sameTbc = (a, b) => !!a.tbc === !!b.tbc;
export async function syncPersonnelChanges(prevList, nextList, projectId) {
  const prev = new Map((prevList || []).map(e => [e.id, e]));
  const next = new Map((nextList || []).map(e => [e.id, e]));
  const jobs = [];
  next.forEach((entry, id) => {
    const old = prev.get(id);
    if (!old) { jobs.push(assignmentCreate(entry, projectId)); return; }
    const changed = PERSISTED_FIELDS.some(k => (old[k] || '') !== (entry[k] || '')) || !sameTbc(old, entry)
      || entryRoles(old).join('|') !== entryRoles(entry).join('|');
    if (changed) jobs.push(assignmentUpdate(entry));
  });
  prev.forEach((entry, id) => { if (!next.has(id)) jobs.push(assignmentDelete(id)); });
  await Promise.all(jobs);
}
